import type { CategorySlug, Product } from "./product-types";
import { img } from "./product-types";
import spinnerYinYangImg from "@/assets/spinner-yin-yang.webp";
import carvDishEagle1Img from "@/assets/carving-dished-eagle-1.webp";
import bs1Img from "@/assets/bs1-red-pink-bottle-stopper.webp";
import smPrayImg from "@/assets/sign-mosaic-pray.webp";
import fp1Img from "@/assets/fp-1-majestic.webp";
import majf1Img from "@/assets/fp-majf-1-majestic.webp";
import drrab2Img from "@/assets/rb-drr-ab2-dragon.webp";
import bapApImg from "@/assets/bap-ap-cartridge.webp";
import ppgSumImg from "@/assets/ppg-sum-sumac-combo.webp";
import ppgMesImg from "@/assets/ppg-mes-mesquite-combo.webp";
import ppgPadImg from "@/assets/ppg-pad-padauk-combo.webp";
import ppgAfzImg from "@/assets/ppg-afz-afzelia-combo.webp";
import cigChr1Img from "@/assets/cig-chr1.webp";
import cornholeRedImg from "@/assets/cornhole-red.webp";

export const homeCategories: Record<
  CategorySlug,
  { slug: CategorySlug; label: string; to: string; blurb: string; image: string; alt: string }
> = {
  pens: {
    slug: "pens",
    label: "Pens and Pencils",
    to: "/custom-pens",
    blurb: "Fountain, rollerball, bolt-action, rifle cartridge, cigar style, pencils and classic ballpoints.",
    image: majf1Img,
    alt: "Majestic fountain pen handcrafted by Paul in figured wood with gold trim",
  },
  "lathe-gifts": {
    slug: "lathe-gifts",
    label: "Lathe-Turned Gifts",
    to: "/lathe-turned-gifts",
    blurb: "Bottle stoppers, duck calls and turned keepsakes shaped one at a time.",
    image: bs1Img,
    alt: "Red and pink cast bottle stopper turned by Paul on a stainless steel base",
  },
  cnc: {
    slug: "cnc",
    label: "CNC & Engraved",
    to: "/cnc-engraved",
    blurb: "Signs, 3D relief carvings and personalized engraving cut in Paul's shop.",
    image: carvDishEagle1Img,
    alt: "CNC-carved dished eagle relief panel made in Paul's shop",
  },
  other: {
    slug: "other",
    label: "Other Creations",
    to: "/other-creations",
    blurb: "Fidget spinners, cornhole boards and the projects that do not fit a category.",
    image: cornholeRedImg,
    alt: "Red custom cornhole boards built and finished by Paul",
  },
  "pen-turners": {
    slug: "pen-turners",
    label: "For Pen Turners",
    to: "/for-pen-turners",
    blurb: "Blanks, components and resources for makers working at their own lathe.",
    image: "/fordite-pen-blank-three-quarter.webp",
    alt: "Fordite pen blank cut square and ready to turn",
  },
};

export const homeFeatured: Product[] = [
  {
    id: "p144",
    featured: true,
    slug: "cig-chr1-chrome-cigar-pen-with-coffee-bean-barrels",
    name: "Chrome Cigar Pen with Coffee Bean Barrels",
    internalCode: "cig-chr1",
    category: "pens",
    subcategory: "Cigar Style",
    shortDescription: "Cigar style pen with chrome trim and fittings and having barrels made with coffee beans embedded in a mint resin.",
    fullDescription: "These pens use a Parker refill and come in a variety of barrel materials and platings. Cigar style pen with chrome trim and fittings and having barrels made with coffee beans embedded in a mint resin.",
    price: 60,
    priceType: "fixed",
    status: "available",
    materials: ["Coffee beans", "Mint resin"],
    finishes: ["Chrome trim"],
    customizable: true,
    images: [img(cigChr1Img, "Chrome Cigar Pen with Coffee Bean Barrels handcrafted by Paul's Pens n Things", 360, 271)],
    tags: ["cigar", "ballpoint", "pens"],
    occasions: ["Birthday", "Graduation", "Just because"],
    relatedProducts: ["p145", "p146", "p147"],
    seoTitle: "Chrome Cigar Pen with Coffee Bean Barrels | Paul's Pens n Things",
    seoDescription: "Cigar style pen with chrome trim and fittings and having barrels made with coffee beans embedded in a mint resin. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p29",
    featured: true,
    slug: "fp-1-majestic-fountain-pen",
    name: "Majestic Fountain Pen in Maple Burl",
    internalCode: "FP-1",
    category: "pens",
    subcategory: "Fountain & Rollerball",
    shortDescription: "A majestic fountain pen with fittings and trim in 22kt gold and rhodium.",
    fullDescription: "A majestic fountain pen with fittings and trim in 22kt gold and rhodium. The barrels are turned from maple burl. This pen has sold; Paul can make something similar in your own materials.",
    price: 215,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Maple burl"],
    finishes: ["22kt gold and rhodium trim"],
    customizable: true,
    images: [img(fp1Img, "Majestic fountain pen in maple burl with 22kt gold and rhodium trim", 360, 271)],
    tags: ["fountain", "wood", "pens"],
    occasions: ["Graduation", "Retirement", "Anniversary"],
    relatedProducts: ["p30", "p31"],
    seoTitle: "Majestic Fountain Pen in Maple Burl | Paul's Pens n Things",
    seoDescription: "A majestic fountain pen with fittings and trim in 22kt gold and rhodium. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p54",
    featured: true,
    slug: "drr-ab2-dragon-rollerball",
    name: "Dragon Rollerball in Resin",
    internalCode: "DRR-AB2",
    category: "pens",
    subcategory: "Fountain & Rollerball",
    shortDescription: "A dragon style rollerball with antique brass trim and fittings, turned from resin.",
    fullDescription: "A dragon style rollerball with antique brass trim and fittings, turned from resin. The clip is shaped like a dragon, a claw surrounds the center band, and a tiger's eye stone is set in the cap. This pen has sold; Paul can make something similar in your own materials.",
    price: 90,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Cast resin", "Tiger's eye stone"],
    finishes: ["Antique brass trim", "Dragon clip and claw centerband"],
    customizable: true,
    images: [img(drrab2Img, "Dragon rollerball in resin with antique brass dragon clip", 360, 271)],
    tags: ["rollerball", "resin", "specialty", "pens"],
    occasions: ["Graduation", "Retirement", "Anniversary"],
    relatedProducts: ["p55", "p56"],
    seoTitle: "Dragon Rollerball in Resin | Paul's Pens n Things",
    seoDescription: "A dragon style rollerball with antique brass trim and fittings, turned from resin. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p65",
    featured: true,
    slug: "bap-ap-bolt-action-cartridge-pen",
    name: "Bolt-Action Cartridge Pen in Camo Resin",
    internalCode: "BAP-AP",
    category: "pens",
    subcategory: "Bolt-Action",
    shortDescription: "A bolt-action cartridge pen with a replica .30 caliber rifle cartridge and a camo resin barrel.",
    fullDescription: "This pen is made with a replica of a 30 caliber rifle cartridge and a camo resin barrel. The finish is antique pewter. The bolt-action mechanism advances and retracts the ink refill, and the pen case is shaped like a gun case.",
    price: 65,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Camo resin", "Replica .30 caliber cartridge"],
    finishes: ["Antique pewter"],
    customizable: true,
    images: [img(bapApImg, "Bolt-action cartridge pen with camo resin barrel and antique pewter finish", 360, 271)],
    tags: ["bolt-action", "rifle cartridge", "resin", "pens"],
    occasions: ["Recognition", "Retirement", "Holiday"],
    relatedProducts: ["p66", "p67", "p68"],
    seoTitle: "Bolt-Action Cartridge Pen in Camo Resin | Paul's Pens n Things",
    seoDescription: "A bolt-action cartridge pen with a replica .30 caliber rifle cartridge and a camo resin barrel. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p161",
    featured: true,
    slug: "bs1-red-pink-bottle-stopper",
    name: "Red / Pink Bottle Stopper",
    internalCode: "BS1",
    category: "lathe-gifts",
    subcategory: "Other Lathe Items",
    shortDescription: "This bottle stopper has a red base and an embedded larger pink porcelain rose.",
    fullDescription: "This bottle stopper has a red base and an embedded larger pink porcelain rose.",
    price: 35,
    priceType: "fixed",
    status: "available",
    materials: ["Red resin base", "Porcelain rose"],
    finishes: ["Stainless stopper"],
    customizable: true,
    images: [img(bs1Img, "Red / Pink Bottle Stopper handcrafted by Paul\'s Pens n Things", 360, 480)],
    tags: ["bottle stopper", "lathe", "kitchen"],
    occasions: ["Wedding", "Holiday", "Just because"],
    relatedProducts: ["p162", "p163", "p160"],
    seoTitle: "Red / Pink Bottle Stopper | Paul\'s Pens n Things",
    seoDescription: "This bottle stopper has a red base and an embedded larger pink porcelain rose. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p173",
    featured: true,
    slug: "mosaic-sign-pray",
    name: "Mosaic Sign — Pray",
    internalCode: "MS-PRAY",
    category: "cnc",
    subcategory: "Signs",
    shortDescription: "The engraving reads \"when in doubt, just pray\".",
    fullDescription: "The engraving reads \"when in doubt, just pray\". These 6 x 6 inch signs are made of 1/4 inch thick, two tone HDPE and are carved on a CNC router. The background and lettering colors can be red, green, blue, brown, yellow, black, or white. Yellow must be paired with black and all other colors are paired with white. Specify the color(s) desired.",
    price: 11,
    priceType: "fixed",
    status: "available",
    materials: ["Two-tone HDPE"],
    finishes: ["CNC carved"],
    customizable: true,
    images: [img(smPrayImg, "Mosaic Sign — Pray carved by Paul\'s Pens n Things", 360, 270)],
    tags: ["sign", "mosaic", "cnc"],
    occasions: ["Housewarming", "Holiday", "Just because"],
    relatedProducts: ["p174", "p175", "p196"],
    seoTitle: "Mosaic Sign — Pray | Paul\'s Pens n Things",
    seoDescription: "The engraving reads \"when in doubt, just pray\". CNC carved in Paragould, Arkansas.",
  },
  {
    id: "p199",
    featured: true,
    slug: "yin-yang-spinner",
    name: "Fidget Spinner — Yin Yang",
    internalCode: "FS-YINYANG",
    category: "cnc",
    subcategory: "Fidget Spinners",
    shortDescription: "This spinner will be in black and white only.",
    fullDescription: "This spinner will be in black and white only. These fidget spinners are cut on a CNC router from a blank of two tone HDPE. There is a ball bearing at the center and counter weights surrounding it. They come in red, green, blue, yellow, brown, black, and white. Yellow must be paired with black and all other colors are paired with white. Specify the color(s) desired.",
    price: 7,
    priceType: "fixed",
    status: "available",
    materials: ["Two-tone HDPE", "Ball bearings"],
    finishes: ["CNC routed"],
    customizable: true,
    images: [img(spinnerYinYangImg, "Fidget Spinner — Yin Yang cut by Paul\'s Pens n Things", 360, 270)],
    tags: ["fidget spinner", "hdpe", "cnc"],
    occasions: ["Birthday", "Stocking stuffer", "Just because"],
    relatedProducts: ["p200", "p201", "p202"],
    seoTitle: "Fidget Spinner — Yin Yang | Paul\'s Pens n Things",
    seoDescription: "This spinner will be in black and white only. CNC routed in Paragould, Arkansas.",
  },
  {
    id: "p252",
    featured: true,
    slug: "cornhole-boards-red",
    name: "Red Cornhole Boards",
    internalCode: "CRN-RED",
    category: "other",
    subcategory: "Cornhole Boards",
    shortDescription: "Regulation 24 by 48 inch set trimmed in red, three coats of polyurethane.",
    fullDescription: "These cornhole boards are hand-crafted from 2x4s and 3/4 inch thick plywood. Trimmed in red with three coats of polyurethane, they measure 24 by 48 inches, the tops are 3/4 inch hardwood plywood, and they meet ACA specifications.",
    price: 250,
    priceType: "fixed",
    status: "previously-created",
    materials: ["2x4 framing lumber", "3/4 inch hardwood plywood top"],
    finishes: ["Three coats of polyurethane", "Red trim"],
    dimensions: "24 x 48 inches (ACA specification)",
    customizable: true,
    images: [img(cornholeRedImg, "Red trimmed handmade cornhole boards by Paul\'s Pens n Things", 360, 270)],
    tags: ["cornhole", "games", "red"],
    occasions: ["Tailgating", "Family gathering", "Graduation gift"],
    relatedProducts: ["p253", "p254", "p255"],
    seoTitle: "Red Cornhole Boards | Paul\'s Pens n Things",
    seoDescription: "Handmade red trimmed cornhole boards, 24 by 48 inches, ACA specification. Built in Paragould, Arkansas.",
  },
];

export const homePrevious: Product[] = [
  {
    id: "p138",
    slug: "cigp-p-cigar-style-pen-and-pencil-set",
    name: "Cigar Style Pen and Pencil Set",
    internalCode: "CIGP&P",
    category: "pens",
    subcategory: "Pen & Pencil Sets",
    shortDescription: "This cigar style pen and pencil set is finished in 24kt gold and made with unknown wood barrels coated in CA.",
    fullDescription: "A matched cigar style pen and pencil set. This cigar style pen and pencil set is finished in 24kt gold and made with unknown wood barrels coated in CA. This set has sold; Paul can make something similar in your own materials.",
    price: 90,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Unknown wood"],
    finishes: ["24kt gold trim", "CA finish"],
    customizable: true,
    featured: false,
    images: [img(ppgMesImg, "Cigar style pen and pencil set in 24kt gold by Paul's Pens n Things", 1200, 900)],
    tags: ["set", "pencil", "wood", "cigar"],
    occasions: ["Retirement", "Graduation", "Anniversary"],
    relatedProducts: ["p139", "p140", "p141"],
    seoTitle: "Cigar Style Pen and Pencil Set | Paul's Pens n Things",
    seoDescription: "A cigar style pen and pencil set finished in 24kt gold with wood barrels coated in CA. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p139",
    slug: "clap-p-classic-style-pen-and-pencil-set",
    name: "Classic Style Pen and Pencil Set",
    internalCode: "CLAP&P",
    category: "pens",
    subcategory: "Pen & Pencil Sets",
    shortDescription: "This classic style pen and pencil set is finished in 24kt gold and made with unknown wood barrels coated in CA.",
    fullDescription: "A matched classic style pen and pencil set. This classic style pen and pencil set is finished in 24kt gold and made with unknown wood barrels coated in CA. This set has sold; Paul can make something similar in your own materials.",
    price: 90,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Unknown wood"],
    finishes: ["24kt gold trim", "CA finish"],
    customizable: true,
    featured: false,
    images: [img(ppgPadImg, "Classic style pen and pencil set in 24kt gold by Paul's Pens n Things", 1200, 900)],
    tags: ["set", "pencil", "wood", "classic"],
    occasions: ["Retirement", "Graduation", "Anniversary"],
    relatedProducts: ["p138", "p140", "p142"],
    seoTitle: "Classic Style Pen and Pencil Set | Paul's Pens n Things",
    seoDescription: "A classic style pen and pencil set finished in 24kt gold with wood barrels coated in CA. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p140",
    slug: "ppg-sum-pen-and-pencil-combo-sumac",
    name: "Pen & Pencil Combo with a Sumac Barrel",
    internalCode: "ppg-sum",
    category: "pens",
    subcategory: "Pen & Pencil Sets",
    shortDescription: "This pen and pencil combo is made of sumac that is finished with CA.",
    fullDescription:
      "Combination pen and pencil. Twist one way it is a pen, twist the other way it is a click pencil. The fittings and trim are finished in 24kt gold. This pen and pencil combo is made of sumac that is finished with CA. This set has sold; Paul can make something similar in your own materials.",
    price: 55,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Sumac"],
    finishes: ["24kt gold trim", "CA finish"],
    customizable: true,
    featured: false,
    images: [img(ppgSumImg, "Pen and pencil combo with a sumac barrel handcrafted by Paul's Pens n Things", 360, 271)],
    tags: ["combo", "pencil", "wood", "set"],
    occasions: ["Retirement", "Graduation", "Just because"],
    relatedProducts: ["p141", "p142", "p143"],
    seoTitle: "Sumac Pen & Pencil Combo | Paul's Pens n Things",
    seoDescription: "A twist pen and click pencil combo turned from sumac with 24kt gold fittings. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p141",
    slug: "ppg-mes-pen-and-pencil-combo-mesquite",
    name: "Pen & Pencil Combo with a Mesquite Barrel",
    internalCode: "ppg-mes",
    category: "pens",
    subcategory: "Pen & Pencil Sets",
    shortDescription: "This pen and pencil combo is made of mesquite that is finished with CA.",
    fullDescription:
      "Combination pen and pencil. Twist one way it is a pen, twist the other way it is a click pencil. The fittings and trim are finished in 24kt gold. This pen and pencil combo is made of mesquite that is finished with CA. This set has sold; Paul can make something similar in your own materials.",
    price: 55,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Mesquite"],
    finishes: ["24kt gold trim", "CA finish"],
    customizable: true,
    featured: false,
    images: [img(ppgMesImg, "Pen and pencil combo with a mesquite barrel handcrafted by Paul's Pens n Things", 360, 271)],
    tags: ["combo", "pencil", "wood", "set"],
    occasions: ["Retirement", "Graduation", "Just because"],
    relatedProducts: ["p140", "p142", "p143"],
    seoTitle: "Mesquite Pen & Pencil Combo | Paul's Pens n Things",
    seoDescription: "A twist pen and click pencil combo turned from mesquite with 24kt gold fittings. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p142",
    slug: "ppg-pad-pen-and-pencil-combo-padauk",
    name: "Pen & Pencil Combo with a Padauk Barrel",
    internalCode: "ppg-pad",
    category: "pens",
    subcategory: "Pen & Pencil Sets",
    shortDescription: "This pen and pencil combo is made of padauk that is finished with CA.",
    fullDescription:
      "Combination pen and pencil. Twist one way it is a pen, twist the other way it is a click pencil. The fittings and trim are finished in 24kt gold. This pen and pencil combo is made of padauk that is finished with CA. This set has sold; Paul can make something similar in your own materials.",
    price: 55,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Padauk"],
    finishes: ["24kt gold trim", "CA finish"],
    customizable: true,
    featured: false,
    images: [img(ppgPadImg, "Pen and pencil combo with a padauk barrel handcrafted by Paul's Pens n Things", 360, 271)],
    tags: ["combo", "pencil", "wood", "set"],
    occasions: ["Retirement", "Graduation", "Just because"],
    relatedProducts: ["p140", "p141", "p143"],
    seoTitle: "Padauk Pen & Pencil Combo | Paul's Pens n Things",
    seoDescription: "A twist pen and click pencil combo turned from padauk with 24kt gold fittings. Handcrafted in Paragould, Arkansas.",
  },
  {
    id: "p143",
    slug: "ppg-afz-pen-and-pencil-combo-afzelia",
    name: "Pen & Pencil Combo with an Afzelia Barrel",
    internalCode: "ppg-afz",
    category: "pens",
    subcategory: "Pen & Pencil Sets",
    shortDescription: "This pen and pencil combo is made of afzelia that is finished with CA.",
    fullDescription:
      "Combination pen and pencil. Twist one way it is a pen, twist the other way it is a click pencil. The fittings and trim are finished in 24kt gold. This pen and pencil combo is made of afzelia that is finished with CA. This set has sold; Paul can make something similar in your own materials.",
    price: 55,
    priceType: "fixed",
    status: "previously-created",
    materials: ["Afzelia"],
    finishes: ["24kt gold trim", "CA finish"],
    customizable: true,
    featured: false,
    images: [img(ppgAfzImg, "Pen and pencil combo with an afzelia barrel handcrafted by Paul's Pens n Things", 360, 271)],
    tags: ["combo", "pencil", "wood", "set"],
    occasions: ["Retirement", "Graduation", "Just because"],
    relatedProducts: ["p140", "p141", "p142"],
    seoTitle: "Afzelia Pen & Pencil Combo | Paul's Pens n Things",
    seoDescription: "A twist pen and click pencil combo turned from afzelia with 24kt gold fittings. Handcrafted in Paragould, Arkansas.",
  },
];

